import {
  PRICE_EXAMPLES,
  PRICING_FRAME_COPY,
  PRICING_DISCLAIMER_SHORT,
} from '@/lib/pricing';
import { PrimaryCTA } from '@/components/PrimaryCTA';

/**
 * Phase 8 refinement: quieter, editorial pricing framing.
 *
 * - Section background: warm stone.
 * - Outer container loses heavy border/shadow.
 * - Price chips: white + warm hairline + tier in champagne uppercase
 *   + amount in display serif tabular-nums (no cyan badges).
 * - Headline in display serif. Eyebrow in champagne uppercase.
 *
 * Every figure is sourced from /lib/pricing.js — one update propagates
 * everywhere. No new claims. Frame is always typical/estimated.
 */

export default function PricingFrameBlock({
  src = 'pricing_frame',
  className = '',
  showHeadline = true,
  showCTA = true,
}) {
  return (
    <section
      className={`bg-[var(--snap-stone-50)] py-20 sm:py-28 ${className}`}
      data-testid="pricing-frame-block"
    >
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        {showHeadline && (
          <div className="max-w-2xl mb-10 sm:mb-12">
            <p className="text-[11px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-[var(--snap-champagne-600)] mb-4">
              How Pricing Works
            </p>
            <h2
              className="font-heading text-[34px] sm:text-[44px] md:text-[52px] leading-[1.05] text-[var(--snap-charcoal)]"
              data-testid="pricing-frame-headline"
            >
              Typical installed price ranges — not a sales pitch.
            </h2>
          </div>
        )}

        <div className="rounded-xl border border-[var(--snap-stone-200)] bg-white">
          <div className="p-6 sm:p-9">
            <p className="text-[15px] sm:text-base text-[var(--snap-slate-500)] leading-relaxed max-w-2xl mb-2">
              <span className="block text-[10.5px] sm:text-[11px] font-semibold tracking-[0.22em] uppercase text-[var(--snap-champagne-600)] mb-2">
                What pricing depends on
              </span>
              {PRICING_FRAME_COPY}
            </p>

            <div className="mt-8 divide-y divide-[var(--snap-stone-200)]">
              {PRICE_EXAMPLES.map((ex) => (
                <div
                  key={ex.id}
                  className="py-6 sm:py-7 grid grid-cols-1 sm:grid-cols-[1fr,auto] gap-3 sm:gap-8 items-start sm:items-center"
                  data-testid={`price-example-${ex.id}`}
                >
                  <div className="text-[15px] sm:text-base text-[var(--snap-charcoal)] leading-snug">
                    {ex.label}
                  </div>
                  <div className="flex flex-wrap gap-2 sm:gap-2.5">
                    {ex.rows.map((r) => (
                      <span
                        key={r.tier}
                        className="inline-flex items-baseline gap-2 rounded-md bg-white border border-[var(--snap-stone-200)] px-3 py-1.5"
                      >
                        <span className="text-[10px] sm:text-[10.5px] uppercase tracking-[0.18em] text-[var(--snap-champagne-600)] font-semibold">
                          {r.tier}
                        </span>
                        <span className="font-heading text-[16px] sm:text-[18px] text-[var(--snap-charcoal)] tabular-nums leading-none">
                          {r.amount}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <p className="text-[12px] sm:text-[12.5px] italic text-[var(--snap-slate-500)] mt-6">
              {PRICING_DISCLAIMER_SHORT}
            </p>
          </div>
        </div>

        {showCTA && (
          <div className="mt-10 sm:mt-12 flex justify-center">
            <PrimaryCTA
              src={src}
              testId="pricing-frame-primary-cta"
              fullWidthMobile={false}
            />
          </div>
        )}
      </div>
    </section>
  );
}
